// ── Elemental type effectiveness ──
// A clean rock-paper-scissors over the eight elements. A super-effective hit does 1.5× damage, a
// not-very-effective one 0.67×. Each element beats two others and is beaten by ~two — tune to taste.
const STRONG_VS = Object.freeze({
  flame: ['verdant', 'frost'],
  tide: ['flame', 'stone'],
  verdant: ['tide', 'stone'],
  frost: ['verdant', 'gale'],
  stone: ['frost', 'gale'],
  gale: ['venom', 'beast'],
  venom: ['verdant', 'beast'],
  beast: ['stone', 'tide'],
});

/**
 * Damage multiplier for an attacker element hitting a defender element.
 * 1.5 = super effective · 0.67 = not very effective · 1 = neutral.
 * @param {string|undefined} attacker
 * @param {string|undefined} defender
 * @returns {number}
 */
export function typeMultiplier(attacker, defender) {
  if (!attacker || !defender || attacker === defender) return 1;
  if (STRONG_VS[attacker]?.includes(defender)) return 1.5;
  if (STRONG_VS[defender]?.includes(attacker)) return 0.67;
  return 1;
}

// ── Level-scaled attack ──
// Caught/spawned/restored monsters store only their species BASE attack — the in-battle level-up loop
// (which adds ~1.5 atk/level) never ran on them. So a lv-40 Leviadon and a lv-3 wild both hit for the
// base value unless we fold the level in at damage time. Folding it here fixes every monster (existing
// and future) with no data migration, symmetrically for player and enemy.

// Per-level stat growth — mirrors the in-battle level-up gains (Between(0,1)+1 ≈ 1.5 atk,
// Between(0,3)+5 ≈ 6.5 hp). The species template stat is treated as the level-1 base, so both grow
// additively and stay positive at every level (no negative HP for low-level apex spawns).
export const ATK_PER_LEVEL = 1.5;
export const HP_PER_LEVEL = 6.5;

/**
 * A monster's effective attack at its current level.
 * @param {number} baseAttack the monster's stored base/species attack
 * @param {number} level the monster's current level
 * @returns {number}
 */
export function attackAtLevel(baseAttack, level) {
  return baseAttack + Math.max(0, (Number(level) || 1) - 1) * ATK_PER_LEVEL;
}

/**
 * A monster's effective max HP at its level (template HP as the level-1 base, like attackAtLevel).
 * @param {number} baseMaxHp the monster's stored base/species max HP
 * @param {number} level the monster's current level
 * @returns {number}
 */
export function hpAtLevel(baseMaxHp, level) {
  return Math.round(baseMaxHp + Math.max(0, (Number(level) || 1) - 1) * HP_PER_LEVEL);
}

/**
 * Short battle caption for a multiplier (null for a neutral hit = no caption).
 * @param {number} mult
 * @returns {string|null}
 */
export function effectivenessLabel(mult) {
  if (mult > 1) return "It's super effective!";
  if (mult < 1) return "It's not very effective…";
  return null;
}
